/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-restricted-globals */

import React, { useEffect, useState } from 'react';
import moment from 'moment';
import Cookies from 'js-cookie';
import { PayPalScriptProvider } from '@paypal/react-paypal-js';
import NumberFormat from 'react-number-format';

import usePayPalAvailability from '../../../hooks/usePayPalAvailability';
import api from '../../../api/api';
import PayPalButtons from '../../../components/PayPalButtons/PayPalButtons';
import PayPalErrorBoundary from '../../../components/PayPalErrorBoundary/PayPalErrorBoundary';
import ApplePayButton from '../../../components/ApplePayButton/ApplePayButton';
import GooglePayButton from '../../../components/GooglePayButton/GooglePayButton';
import NmiApplePayButton from '../../../components/NmiApplePayButton/NmiApplePayButton';
import NmiGooglePayButton from '../../../components/NmiGooglePayButton/NmiGooglePayButton';
import Banner from '../../../components/Banner/Banner';
import ColorSelect from '../../../components/ColorSelect/ColorSelect';
import CrossSells from '../../../components/CrossSells/CrossSells';
import MoneyBackGuarantee from '../../../components/MoneyBackGuarantee/MoneyBackGuarantee';
import Reviews from '../../../components/Reviews/Reviews';
import VariantSelect from '../../../components/VariantSelect/VariantSelect';

import {
  buildQueryParams,
  getMaxDiscount,
  getPriceWithCurrency,
  getSavingsPercent,
  makePlural,
} from '../../../utils/helpers';
import { states } from '../../../utils/states';

import visa from './img/visa.webp';
import master from './img/master.webp';
import amex from './img/amex.webp';
import safe from './img/safe.webp';

import styles from './Default.module.sass';

function getFbcValue(queryParams) {
  if (Cookies.get('_fbc')) {
    return Cookies.get('_fbc');
  }

  if (queryParams.fbclid) {
    const timestamp = new Date().getTime();
    return `fb.1.${timestamp}.${queryParams.fbclid}`;
  }

  return '';
}

const validationMessages = {
  email: 'Enter valid email address.',
  phone: 'Enter valid phone number.',
  creditCardNumber: 'Enter valid credit card number.',
  firstName: 'First name is required.',
  lastName: 'Last name is required.',
  shippingAddress1: 'Shipping address is required.',
  shippingCity: 'City is required.',
  shippingState: 'This Field is required.',
  shippingZip: 'ZIP code is required.',
  expirationMonth: 'Select a valid expiration month.',
  expirationYear: 'Select a valid expiration year.',
  CVV: 'Enter valid CVV.',
  isTermsAgreed: 'You must agree to the terms.',
};

// Validate a single field
function validateField(fieldName, value, shippingCountry) {
  switch (fieldName) {
    case 'email':
      // Regex to allow + and enforce a general email structure.
      // - Allows '+' in the local part (e.g., user+tag@example.com).
      // - Ensures the email doesn't end with a period due to \.[a-zA-Z]{2,}.
      // - Ensures only one '@' by its inherent structure.
      const emailFormatRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!value || !emailFormatRegex.test(value)) {
        return validationMessages.email;
      }

      // Additional check for double periods in the domain part (e.g., user@example..com)
      // The regex [a-zA-Z0-9.-]+ for the domain part might allow this, so an explicit check is safer.
      const domainPart = value.substring(value.lastIndexOf('@') + 1);
      if (domainPart.includes('..')) {
        return validationMessages.email;
      }
      break;
    case 'phone':
      if (!value) {
        return validationMessages.phone;
      }

      if (shippingCountry === 'GB') {
        // Validate UK phone numbers in international format
        if (!/^\+44\d{10}$/.test(value.replace(/\s+/g, ''))) {
          return 'Enter a valid UK phone number.';
        }
      } else if (shippingCountry === 'US') {
        // Validate US phone numbers in international format
        if (!/^\+1\d{10}$/.test(value.replace(/\s+/g, ''))) {
          return 'Enter a valid US phone number.';
        }
      }
      break;
    case 'creditCardNumber':
      if (!value || !/^\d{13,19}$/.test(value.replace(/\s+/g, ''))) {
        return validationMessages.creditCardNumber;
      }
      break;
    case 'firstName':
      if (!value) {
        return validationMessages.firstName;
      }
      break;
    case 'lastName':
      if (!value) {
        return validationMessages.lastName;
      }
      break;
    case 'shippingAddress1':
      if (!value) {
        return validationMessages.shippingAddress1;
      }
      break;
    case 'shippingCity':
      if (!value) {
        return validationMessages.shippingCity;
      }
      break;
    case 'shippingState':
      if (!value) {
        return validationMessages.shippingState;
      }
      break;
    case 'shippingZip':
      if (!value) {
        return validationMessages.shippingZip;
      }
      if (shippingCountry === 'US') {
        if (!/^[0-9]+$/.test(value)) {
          return 'US ZIP code must contain only numbers.';
        }
        if (value.length !== 5) {
          return 'US ZIP code must be 5 digits.';
        }
      }
      break;
    case 'expirationMonth':
      if (!value || isNaN(value) || value < 1 || value > 12) {
        return validationMessages.expirationMonth;
      }
      break;
    case 'expirationYear':
      if (!value || isNaN(value)) {
        return validationMessages.expirationYear;
      }
      break;
    case 'CVV':
      if (!value || !/^\d{3,4}$/.test(value)) {
        return validationMessages.CVV;
      }
      break;
    case 'isTermsAgreed':
      if (!value) {
        return validationMessages.isTermsAgreed;
      }
      break;
    default:
      break;
  }

  return null; // No error
}

const validateForm = (data, shippingCountry) => {
  const errors = {};
  Object.keys(validationMessages).forEach((field) => {
    const error = validateField(field, data[field], shippingCountry);
    if (error) {
      errors[field] = error;
    }
  });

  // Additional validation for combined expiration date
  if (!errors.expirationMonth && !errors.expirationYear) {
    const expDate = moment(`${data.expirationMonth}-${data.expirationYear}`, 'MM-YYYY', true).endOf('month');
    const currentDate = moment();

    if (!expDate.isValid() || expDate.isBefore(currentDate, 'day')) {
      errors.expirationMonth = validationMessages.expirationMonth;
      errors.expirationYear = validationMessages.expirationYear;
    }
  }
  return errors;
};

export default function Default({
  offer,
  isLoading,
  crossSells,
  onTermsClick,
  onCreateOrder,
  declineError,
  reviews,
  showReviews,
  isCooldownActive,
}) {
  const [banners, setBanners] = useState([]);
  const queryParams = buildQueryParams(location);
  const showPayPal = queryParams.paypal !== '0' && (queryParams.paypal === '1' || offer.showPayPal);
  const showApplePay = queryParams.applepay !== '0' && (queryParams.applepay === '1' || offer.showApplePay);
  const showGooglePay =
    queryParams.googlepay !== '0' && (queryParams.googlepay === '1' || offer.showGooglePay);
  const isVariantSelectorTest = queryParams.variantselectortest === '1';

  const mergedProductsData = offer.productsData.map((product) => {
    const productDetail = offer.productDetails[product.id];

    return {
      ...product,
      ...productDetail,
    };
  });

  const defaultProduct = mergedProductsData.find(
    (item) => String(item?.id) === String(offer?.defaultVariantId),
  );

  const preSelectedProduct = mergedProductsData.find(
    (item) => String(item?.id) === String(queryParams?.productid),
  );

  const initialProduct = preSelectedProduct || defaultProduct || mergedProductsData[0];

  const defaultVariantId = offer.defaultVariantId;
  const someProductIsSubscription = mergedProductsData.some((item) => item.billingModelId !== '2');

  // Determine initial offer type based on the initial product
  const initialOfferType = initialProduct?.billingModelId === '2' ? 'one-time' : 'subscription';

  const [clientToken, setClientToken] = useState(null);
  const { isPayPalEnabled, isLoading: isPayPalLoading } = usePayPalAvailability(clientToken);

  // Initialize selectedProduct and selectedVariant directly using initialProduct
  const [selectedProduct, setSelectedProduct] = useState(initialProduct);
  const [selectedVariant, setSelectedVariant] = useState({
    // Use color or variantValue for name
    name: initialProduct?.color || initialProduct?.variantValue,
    // Use colorImage, colorimage, or variantImage for imageSrc
    imageSrc: initialProduct?.colorImage || initialProduct?.colorimage || initialProduct?.variantImage,
  });
  // Initialize selectedOfferType based on initialProduct
  const [selectedOfferType, setSelectedOfferType] = useState(initialOfferType);
  const [isSizeGuideOpen, setIsSizeGuideOpen] = useState(false);

  const [errors, setErrors] = useState({});
  const [timeLeft, setTimeLeft] = useState({
    hours: 0,
    minutes: 15,
    seconds: 0,
  });

  const isSelectedProductSubscription = selectedProduct?.billingModelId !== '2';

  const [state, setState] = useState({
    email: '',
    firstName: '',
    lastName: '',
    shippingAddress1: '',
    shippingCity: '',
    shippingState: 'AL',
    shippingCountry: offer?.shippingCountries[0]?.id || 'US',
    shippingZip: '',
    phone: '',
    creditCardNumber: '',
    expirationMonth: '',
    expirationYear: '',
    CVV: '',
    billingFirstName: '',
    billingLastName: '',
    billingAddress1: '',
    billingCity: '',
    billingState: '',
    billingCountry: 'US',
    billingZip: '',
    billingSameAsShipping: true,
    brand: offer.brand,
    isTermsAgreed: true,
    productId: initialProduct.id,
    productQty: initialProduct.quantity,
    productName: initialProduct.name,
    productPrice: initialProduct.price,
    agreeSms: false,
    fbc: getFbcValue(queryParams),
    fbp: Cookies.get('_fbp'),
    fbclid: queryParams.fbclid || '',
    orderTotal: initialProduct.price * 1 + initialProduct.shippingPrice * 1,
    campaignId: offer.campaignId,
    shippingId: initialProduct.shippingId || '',
    shippingPrice: initialProduct.shippingPrice || '',
    offers: [
      {
        product_id: initialProduct.id,
        offer_id: initialProduct.offerId,
        billing_model_id: initialProduct.billingModelId,
        price: initialProduct.price,
        productQty: initialProduct.quantity,
        productName: initialProduct.name,
        isMainProduct: true,
        ...(initialProduct?.trial?.enabled && {
          trial: {
            ...(initialProduct.trial.productId && { product_id: initialProduct.trial.productId }),
            ...(initialProduct.trial.price && { price: initialProduct.trial.price }),
            ...(initialProduct.trial.quantity && { quantity: initialProduct.trial.quantity }),
          },
        }),
      },
    ],
  });

  async function getBanners() {
    try {
      const response = await api.get('/banners');
      if (response.data) {
        setBanners(response.data);
      }
      return response.data;
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  useEffect(() => {
    if (!offer.isBannerDisabled) {
      getBanners();
    }

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    // Re-find the default product in case mergedProductsData changed structure
    const currentDefaultProduct =
      mergedProductsData.find((item) => String(item.id) === String(queryParams.productid)) ||
      mergedProductsData.find((item) => String(item.id) === String(defaultVariantId)) ||
      mergedProductsData[0];

    // If the currently selected product is no longer valid or doesn't match the default, reset
    // (This logic might need adjustment based on exact requirements)
    if (!selectedProduct || String(selectedProduct.id) !== String(currentDefaultProduct.id)) {
      const productToSelect = currentDefaultProduct;
      setSelectedProduct(productToSelect);
      setSelectedVariant({
        name: productToSelect?.color || productToSelect?.variantValue,
        imageSrc: productToSelect?.colorImage || productToSelect?.colorimage || productToSelect?.variantImage,
      });
      setSelectedOfferType(productToSelect?.billingModelId === '2' ? 'one-time' : 'subscription');
    }
  }, [offer.productsData]); // Keep dependency on offer.productsData

  useEffect(() => {
    // Guard clause: Don't run this effect until initial product/variant are set.
    if (!selectedProduct || !selectedVariant?.name) return;

    const filteredProducts = filterProducts(mergedProductsData);

    // Try to find a product matching the current quantity, selected variant color, and offer type
    let newSelectedProduct = filteredProducts.find(
      (item) =>
        item.quantity === selectedProduct?.quantity &&
        (item.color || item.variantValue) === selectedVariant?.name &&
        (someProductIsSubscription
          ? item.billingModelId ===
            (selectedOfferType === 'subscription' ? selectedProduct?.billingModelId : '2')
          : true),
    );

    // Fallback to the first available product in the filtered list if no match found
    if (!newSelectedProduct && filteredProducts.length > 0) {
      newSelectedProduct = filteredProducts[0];
    }

    if (
      newSelectedProduct &&
      newSelectedProduct?.id !== selectedProduct?.id &&
      newSelectedProduct.soldOut !== '1'
    ) {
      setSelectedProduct(newSelectedProduct);
    } else if (newSelectedProduct?.soldOut === '1') {
      setSelectedProduct(null); // Or handle sold out differently
    }
  }, [selectedOfferType, selectedVariant]);

  useEffect(() => {
    // Guard clause: Don't run this effect until initial product is set.
    if (!selectedProduct) return;

    // This effect specifically handles switching between one-time/subscription for the *same quantity*
    const correspondingProduct = mergedProductsData.find(
      (item) =>
        item.quantity === selectedProduct?.quantity &&
        (item.color || item.variantValue) === selectedVariant?.name && // Ensure color matches too
        item.id !== selectedProduct?.id && // Must be a different product ID (implying different billing model)
        (selectedOfferType === 'subscription' ? item.billingModelId !== '2' : item.billingModelId === '2'),
    );

    if (correspondingProduct && correspondingProduct.id !== selectedProduct.id) {
      setSelectedProduct(correspondingProduct);
    }
  }, [selectedOfferType]); // Keep dependency only on selectedOfferType

  useEffect(() => {
    const stickyOffer = {
      billing_model_id: selectedProduct?.billingModelId,
      offer_id: selectedProduct?.offerId,
      product_id: selectedProduct?.id,
      price: selectedProduct?.price,
      productQty: selectedProduct?.quantity,
      productName: selectedProduct?.name,
      isMainProduct: true,
      ...(selectedProduct?.trial?.enabled && {
        trial: {
          ...(selectedProduct.trial.productId && { product_id: selectedProduct.trial.productId }),
          ...(selectedProduct.trial.price && { price: selectedProduct.trial.price }),
          ...(selectedProduct.trial.quantity && { quantity: selectedProduct.trial.quantity }),
        },
      }),
    };

    const currentOffers = state.offers || [];
    const newOffers = currentOffers.filter((offer) => !offer.isMainProduct);
    newOffers.push(stickyOffer);

    const offersTotal = newOffers.reduce((total, offer) => total + offer.price * 1, 0);
    const orderTotal = offersTotal + selectedProduct?.shippingPrice * 1;

    setState({
      ...state,
      orderTotal,
      offers: newOffers,
      shippingId: selectedProduct?.shippingId,
      shippingPrice: selectedProduct?.shippingPrice,
      productId: selectedProduct?.id,
      productQty: selectedProduct?.quantity,
      productName: selectedProduct?.name,
      productPrice: selectedProduct?.price,
    });
  }, [selectedProduct]);

  useEffect(() => {
    if (crossSells.length > 0) {
      // Get preselected cross-sells from offer config
      const preSelectedFromOffer = crossSells.filter((item) =>
        item.preselectedOffers.some((o) => o.id === offer._id),
      );

      // Get preselected cross-sells from URL parameter (supports comma-separated IDs)
      const urlCrossSellIds = queryParams.crosssellid
        ? queryParams.crosssellid.split(',').map((id) => id.trim())
        : [];
      const preSelectedFromUrl = crossSells.filter((item) => urlCrossSellIds.includes(String(item._id)));

      // Combine both sources, avoiding duplicates
      const preSelectedIds = new Set(preSelectedFromOffer.map((item) => item._id));
      const combinedPreSelected = [
        ...preSelectedFromOffer,
        ...preSelectedFromUrl.filter((item) => !preSelectedIds.has(item._id)),
      ];

      if (combinedPreSelected.length > 0) {
        const crossSellOffers = [];

        combinedPreSelected.forEach((item) => {
          let itemToAdd = item;

          if (item.variants && item.variants.length > 0) {
            itemToAdd = item.variants[0];
          }

          crossSellOffers.push({
            billing_model_id: itemToAdd.productBillingModelId,
            offer_id: itemToAdd.productOfferId,
            product_id: itemToAdd.productId,
            price: itemToAdd.price,
            productName: itemToAdd.productName || itemToAdd.name,
            productQty: '1',
            isCrossSell: true,
            ...(item.trial?.enabled && {
              trial: {
                product_id: item.trial.productId,
                price: item.trial.price,
                quantity: item.trial.quantity,
              },
            }),
          });
        });

        setState((prevState) => {
          const currentOffers = prevState.offers || [];
          const offersWithoutPreselectedCrossSells = currentOffers.filter(
            (offer) => !(offer.isCrossSell && offer.preselected),
          );
          const newOffers = [...offersWithoutPreselectedCrossSells, ...crossSellOffers];

          const offersTotal = newOffers.reduce((total, offer) => total + offer.price * 1, 0);
          const orderTotal = offersTotal + selectedProduct?.shippingPrice * 1;

          return {
            ...prevState,
            orderTotal,
            offers: newOffers,
          };
        });
      }
    }
  }, [crossSells]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setTimeLeft((prevTime) => {
        let { hours, minutes, seconds } = prevTime;

        if (seconds > 0) {
          seconds -= 1;
        } else if (minutes > 0) {
          seconds = 59;
          minutes -= 1;
        } else if (hours > 0) {
          seconds = 59;
          minutes = 59;
          hours -= 1;
        } else {
          clearInterval(intervalId); // Stop the countdown when it reaches zero
        }

        return { hours, minutes, seconds };
      });
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const merchantAccountId = offer.braintreeMerchantAccountId || offer.payPalMerchantAccountId;
    const needsToken = (showPayPal || showApplePay || showGooglePay) && merchantAccountId;

    if (!needsToken) return;

    (async () => {
      try {
        const { data } = await api.get(`/braintree/token?merchantAccountId=${merchantAccountId}`);
        setClientToken(data.clientToken);
      } catch (error) {
        console.error('Failed to fetch Braintree token:', error);
      }
    })();
  }, [
    offer.braintreeMerchantAccountId,
    offer.payPalMerchantAccountId,
    showPayPal,
    showApplePay,
    showGooglePay,
  ]);

  useEffect(() => {
    if (state.shippingCountry === 'US' || state.shippingCountry === 'CA') {
      setState({ ...state, shippingState: 'AL' });
    } else {
      setState({ ...state, shippingState: '' });
    }
  }, [state.shippingCountry]);

  // Helpers

  function updateOffers(item, meta) {
    const newOffers = [...state.offers];
    const existingItem = state.offers.find((o) => o.product_id * 1 === item.productId * 1);

    if (existingItem) {
      newOffers.splice(newOffers.indexOf(existingItem), 1);
    } else {
      newOffers.push({
        billing_model_id: item.productBillingModelId,
        offer_id: item.productOfferId,
        product_id: item.productId,
        price: item.price,
        productName: item.productName || item.name,
        ...(item.trial?.enabled && {
          trial: {
            product_id: item.trial.productId,
            price: item.trial.price,
            quantity: item.trial.quantity,
          },
        }),
        ...meta,
      });
    }

    setState({ ...state, offers: newOffers });
  }

  function filterProductsByVariant(items) {
    if (!selectedVariant) {
      return items;
    }

    const variantName = selectedVariant.name || selectedVariant.variantValue;

    return items.filter((item) => (item.color || item.variantValue) === variantName);
  }

  function filterByOfferType(items) {
    if (someProductIsSubscription) {
      return items.filter((item) => {
        if (selectedOfferType === 'subscription') {
          return item.billingModelId !== '2';
        }

        return item.billingModelId === '2';
      });
    }

    return items;
  }

  function filterProducts(items) {
    const filteredByVariant = filterProductsByVariant(items);
    return filterByOfferType(filteredByVariant);
  }

  function getSavings() {
    const qty = selectedProduct?.quantity;

    if (!qty) {
      return 0;
    }

    const oneTimeProduct = mergedProductsData.find(
      (item) => item.billingModelId === '2' && item.quantity === qty,
    );
    const subscriptionProduct = mergedProductsData.find(
      (item) => item.billingModelId !== '2' && item.quantity === qty,
    );

    return getPriceWithCurrency(
      oneTimeProduct?.price * 1 + oneTimeProduct?.shippingPrice * 1 - subscriptionProduct?.price * 1,
      offer?.locale,
      offer?.currency,
    );
  }

  // Handlers

  function handleVariantSelect(variant) {
    setSelectedVariant(variant);
    const newProduct = mergedProductsData.find(
      (item) =>
        (item.color || item.variantValue) === variant.name && item.quantity === selectedProduct?.quantity,
    );

    if (newProduct?.soldOut === '1') {
      setSelectedProduct(null);
    }

    setSelectedProduct(newProduct);
  }

  function handleFieldBlur(fieldName) {
    const errorMessage = validateField(fieldName, state[fieldName], state.shippingCountry);
    setErrors((prevErrors) => ({
      ...prevErrors,
      [fieldName]: errorMessage,
    }));
  }

  function handleCrossSellChange(crossSell) {
    updateOffers(crossSell, { isCrossSell: true });
  }

  function handleExpressApprove(data) {
    handleCreateOrder(data);
  }

  function handleFormSubmit() {
    if (isCooldownActive) return;

    const validationErrors = validateForm(state, state.shippingCountry);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      handleCreateOrder(state);
    } else {
      const firstErrorField = Object.keys(validationErrors)[0];
      const fieldElement = document.querySelector(`[name="${firstErrorField}"]`);
      if (fieldElement) {
        fieldElement.focus();
      }
    }
  }

  function handleCreateOrder(data) {
    const stickyOffer = {
      billing_model_id: selectedProduct?.billingModelId,
      offer_id: selectedProduct?.offerId,
      product_id: selectedProduct?.id,
      price: selectedProduct?.price,
      productQty: selectedProduct?.quantity,
      productName: selectedProduct?.name,
      isMainProduct: true,
      ...(selectedProduct?.trial?.enabled && {
        trial: {
          ...(selectedProduct.trial.productId && { product_id: selectedProduct.trial.productId }),
          ...(selectedProduct.trial.price && { price: selectedProduct.trial.price }),
          ...(selectedProduct.trial.quantity && { quantity: selectedProduct.trial.quantity }),
        },
      }),
    };

    const currentOffers = state.offers || [];
    const newOffers = currentOffers.filter((offer) => !offer.isMainProduct);
    newOffers.push(stickyOffer);

    const offersTotal = newOffers.reduce((total, offer) => total + offer.price * 1, 0);
    const orderTotal = offersTotal + selectedProduct?.shippingPrice * 1;
    const expirationDate = `${state.expirationMonth}${state.expirationYear.slice(-2)}`;

    const orderData = {
      ...data,
      expirationDate,
      orderTotal,
      offers: newOffers,
      shippingId: selectedProduct?.shippingId,
      shippingPrice: selectedProduct?.shippingPrice,
      productId: selectedProduct?.id,
      productQty: selectedProduct?.quantity,
      productName: selectedProduct?.name,
      productPrice: selectedProduct?.price,
    };

    if (!selectedProduct) {
      setErrors({ ...errors, product: 'Please select a product' });
      return;
    } else {
      setErrors({ ...errors, product: null });
    }

    onCreateOrder(orderData);
  }

  function renderStateSelect() {
    // Remove restricted states from states if SolVolt offer
    if (queryParams.offerid === '66461c98e81a190011fb3092') {
      const restrictedStates = ['CT', 'HI', 'IL', 'IN', 'MA', 'MI', 'NC', 'RI', 'VI', 'WA', 'DC'];

      for (const state in states['US']) {
        if (restrictedStates.includes(state)) {
          delete states['US'][state];
        }
      }
    }

    if (state.shippingCountry === 'US' || state.shippingCountry === 'CA') {
      return (
        <div className={styles.inputGroup}>
          <label htmlFor='shippingState'>State/Province</label>
          <div className={styles.formSelect}>
            <select
              name='shippingState'
              value={state.shippingState}
              onChange={(e) => {
                setState({ ...state, shippingState: e.target.value });
                setErrors((prevErrors) => ({ ...prevErrors, shippingState: null }));
              }}
              onBlur={() => handleFieldBlur('shippingState')}
            >
              {Object.entries(states[state.shippingCountry]).map(([code, name], index) => {
                return (
                  <option key={index} value={code}>
                    {name}
                  </option>
                );
              })}
            </select>
          </div>
          {errors.shippingState && <div className={styles.errorMessage}>{errors.shippingState}</div>}
        </div>
      );
    }

    return (
      <div className={styles.inputGroup}>
        <label htmlFor='shippingState'>County/District/Region</label>
        <input
          placeholder='County/District/Region'
          type='text'
          name='shippingState'
          value={state.shippingState}
          className={errors.shippingState ? styles.errorInput : ''}
          onChange={(e) => {
            setState({ ...state, shippingState: e.target.value });
            setErrors((prevErrors) => ({ ...prevErrors, shippingState: null }));
          }}
          onBlur={() => handleFieldBlur('shippingState')}
        />

        {errors.shippingState && <div className={styles.errorMessage}>{errors.shippingState}</div>}
      </div>
    );
  }

  // Render

  // Find the variant name from the first product that has one
  const variantTitle = mergedProductsData.find((p) => p.variantName)?.variantName;

  function renderProducts() {
    if (preSelectedProduct) {
      return (
        <>
          <div className={styles.stepTitle}>
            <i className='fa-solid fa-gift'></i> Selected Product
          </div>
          <div className={styles.products}>
            <div
              key={preSelectedProduct.id}
              className={`${styles.product} ${
                selectedProduct?.id === preSelectedProduct.id && styles.productActive
              } ${preSelectedProduct.soldOut === '1' && styles.productSoldOut} ${styles.productPreSelected}`}
              onClick={() => setSelectedProduct(preSelectedProduct)}
              style={{ width: '100%' }}
            >
              {preSelectedProduct.mostPopular && <div className={styles.productPopular}>Most Popular</div>}
              {preSelectedProduct.bestValue && <div className={styles.productBestValue}>Best Value</div>}
              {preSelectedProduct.soldOut === '1' && (
                <div className={styles.productSoldOutBadge}>Sold Out</div>
              )}
              <div className={styles.productImage}>
                <img src={preSelectedProduct.image} alt={offer.productName} />
              </div>
              <div className={styles.productContent}>
                <b>{preSelectedProduct.quantity}x</b>
                {preSelectedProduct.subtitle && (
                  <p className={styles.productSubtitle}>{preSelectedProduct.subtitle}</p>
                )}
                <p
                  dangerouslySetInnerHTML={{
                    __html: makePlural(
                      offer.productName,
                      preSelectedProduct.quantity * 1,
                      offer.productNameSingular,
                    ),
                  }}
                ></p>
                <s>{getPriceWithCurrency(preSelectedProduct.oldPrice, offer?.locale, offer?.currency)}</s>
                <span>{getPriceWithCurrency(preSelectedProduct.price, offer?.locale, offer?.currency)}</span>
              </div>
            </div>
          </div>
        </>
      );
    }
    return (
      <>
        <div className={styles.stepTitle}>
          <i className='fa-solid fa-gift'></i> Select Quantity
        </div>
        {someProductIsSubscription && (
          <div className={styles.offerSelector}>
            <button
              className={`${styles.offerSelectorButton} ${
                selectedOfferType === 'one-time' && styles.offerSelectorButtonActive
              }`}
              onClick={() => setSelectedOfferType('one-time')}
            >
              One-Time Purchase
            </button>
            <button
              className={`${styles.offerSelectorButton} ${
                selectedOfferType === 'subscription' && styles.offerSelectorButtonActive
              }`}
              onClick={() => setSelectedOfferType('subscription')}
            >
              <p>
                Subscribe & <span>save extra {getSavings()}</span>
              </p>
              <b>+FREE SHIPPING ALWAYS</b>
            </button>
          </div>
        )}
        <div className={styles.products}>
          {filterProducts(mergedProductsData).map((product) => (
            <div
              key={product.id}
              className={`${styles.product} ${selectedProduct?.id === product.id && styles.productActive} ${
                product.soldOut === '1' && styles.productSoldOut
              }`}
              onClick={() => setSelectedProduct(product)}
            >
              {product.mostPopular && <div className={styles.productPopular}>Most Popular</div>}
              {product.bestValue && <div className={styles.productBestValue}>Best Value</div>}
              {product.soldOut === '1' && <div className={styles.productSoldOutBadge}>Sold Out</div>}
              <div className={styles.productImage}>
                <img src={product.image} alt={offer.productName} />
              </div>
              <div className={styles.productContent}>
                <b>{product.quantity}x</b>
                {product.subtitle && <p className={styles.productSubtitle}>{product.subtitle}</p>}
                <p
                  dangerouslySetInnerHTML={{
                    __html: makePlural(offer.productName, product.quantity * 1, offer.productNameSingular),
                  }}
                ></p>
                <s>{getPriceWithCurrency(product.oldPrice, offer?.locale, offer?.currency)}</s>
                <span>{getPriceWithCurrency(product.price, offer?.locale, offer?.currency)}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Color Select */}
        {selectedVariant && selectedVariant.name && !isVariantSelectorTest && (
          <ColorSelect
            title={variantTitle ? `Choose Your ${variantTitle}` : 'Choose Your Option'}
            products={mergedProductsData}
            onChange={handleVariantSelect}
            selectedVariant={selectedVariant}
            defaultVariantId={defaultVariantId}
          />
        )}
        {selectedVariant && selectedVariant.name && isVariantSelectorTest && (
          <VariantSelect
            title={variantTitle ? `Choose Your ${variantTitle}` : 'Choose Your Option'}
            products={mergedProductsData}
            onChange={handleVariantSelect}
            selectedVariant={selectedVariant}
            defaultVariantId={defaultVariantId}
            sizeGuide={
              offer.sizeGuideImage && (
                <div className={styles.sizeGuideContainer}>
                  <button onClick={() => setIsSizeGuideOpen(true)}>Size guide</button>
                </div>
              )
            }
          />
        )}

        {!isVariantSelectorTest && offer.sizeGuideImage && (
          <div className={styles.sizeGuideContainer}>
            <button onClick={() => setIsSizeGuideOpen(true)}>Size guide</button>
          </div>
        )}
      </>
    );
  }

  return (
    <div className='default-template'>
      <Banner banners={banners} discount={getMaxDiscount(offer)} />
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.container}>
          <div className={styles.headerInner}>
            <div className={styles.headerLeft}>
              <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'>
                <path d='M256 0c36.8 0 68.8 20.7 84.9 51.1C373.8 41 411 49 437 75s34 63.3 23.9 96.1C491.3 187.2 512 219.2 512 256s-20.7 68.8-51.1 84.9C471 373.8 463 411 437 437s-63.3 34-96.1 23.9C324.8 491.3 292.8 512 256 512s-68.8-20.7-84.9-51.1C138.2 471 101 463 75 437s-34-63.3-23.9-96.1C20.7 324.8 0 292.8 0 256s20.7-68.8 51.1-84.9C41 138.2 49 101 75 75s63.3-34 96.1-23.9C187.2 20.7 219.2 0 256 0zM192 224a32 32 0 1 0 0-64 32 32 0 1 0 0 64zm160 96a32 32 0 1 0 -64 0 32 32 0 1 0 64 0zM337 209c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0L175 303c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0L337 209z' />
              </svg>
              <p>
                Hurry! <b>{getSavingsPercent(selectedProduct?.oldPrice, selectedProduct?.price)}% discount</b>{' '}
                reserved for:
              </p>
            </div>
            <div className={styles.headerRight}>
              <div className={styles.headerRightTime}>
                <b>{String(timeLeft.hours).padStart(2, '0')}</b> <p>HOURS</p>
              </div>
              <b>:</b>
              <div className={styles.headerRightTime}>
                <b>{String(timeLeft.minutes).padStart(2, '0')}</b> <p>MINUTES</p>
              </div>
              <b>:</b>
              <div className={styles.headerRightTime}>
                <b>{String(timeLeft.seconds).padStart(2, '0')}</b>
                <p>SECONDS</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className={styles.hero}>
        <div className={styles.container}>
          <div className={styles.heroLogo}>
            <img src={offer.logoSrc} alt={offer.brand} />
          </div>
        </div>
      </section>
      {/* Checkout */}
      <div className={styles.container}>
        <section className={styles.checkout}>
          {/* Checkout Left */}
          <article className={styles.checkoutLeft}>
            {/* Benefits and Discount */}
            <div className={styles.benefitsAndDiscount}>
              <div className={styles.benefits}>
                {offer.benefitsWithIcons.map((benefit, index) => (
                  <div key={index} className={styles.benefit}>
                    <img src={benefit.icon} alt={benefit.name} />
                    <p>{benefit.name}</p>
                  </div>
                ))}
              </div>
              <div className={styles.discount}>
                <div className={styles.discountImage}>
                  <p>{getSavingsPercent(selectedProduct?.oldPrice, selectedProduct?.price)}% OFF</p>
                </div>
                <div className={styles.discountContent}>
                  <p style={{ color: 'red' }}>
                    YOUR {getSavingsPercent(selectedProduct?.oldPrice, selectedProduct?.price)}% DISCOUNT
                    APPLIED SUCCESSFULLY
                  </p>
                  <p>
                    {offer.productName} is available at the price of:{' '}
                    <s>{getPriceWithCurrency(selectedProduct?.oldPrice, offer?.locale, offer?.currency)}</s>{' '}
                    <b>{getPriceWithCurrency(selectedProduct?.price, offer?.locale, offer?.currency)}</b> (
                    {getSavingsPercent(selectedProduct?.oldPrice, selectedProduct?.price)}% Discount)
                  </p>
                </div>
              </div>
            </div>
            {/* Step 1 */}
            <div className={styles.step1}>
              {/* Products */}
              {renderProducts()}

              {crossSells.length > 0 && (
                <CrossSells
                  offer={offer}
                  offers={state.offers}
                  crossSells={crossSells}
                  onChange={handleCrossSellChange}
                />
              )}
            </div>
          </article>
          {/* Checkout Right */}
          <article className={styles.checkoutRight}>
            {/* Express Checkout */}
            {clientToken &&
              clientToken.startsWith('eyJ') &&
              (showPayPal || showApplePay || showGooglePay) && (
                <>
                  <div className={styles.expressCheckout}>
                    <span className={styles.expressCheckoutTitle}>Express Checkout</span>
                    <div className={styles.expressCheckoutButtons}>
                      {showPayPal && !isPayPalLoading && isPayPalEnabled && (
                        <PayPalErrorBoundary>
                          <PayPalScriptProvider
                            options={{
                              vault: true,
                              clientId: process.env.REACT_APP_PAYPAL_CLIENT_ID,
                              components: 'buttons',
                              dataClientToken: clientToken,
                            }}
                          >
                            <PayPalButtons
                              offer={offer}
                              orderData={state}
                              checkoutTotal={state.orderTotal}
                              onApprove={handleExpressApprove}
                              isSelectedProductSubscription={isSelectedProductSubscription}
                            />
                          </PayPalScriptProvider>
                        </PayPalErrorBoundary>
                      )}
                      {showApplePay &&
                        (offer.paymentGateway === 'nmi' ? (
                          <NmiApplePayButton
                            checkoutTotal={state.orderTotal}
                            orderData={state}
                            offer={offer}
                            onApprove={handleExpressApprove}
                          />
                        ) : (
                          <ApplePayButton
                            clientToken={clientToken}
                            checkoutTotal={state.orderTotal}
                            orderData={state}
                            offer={offer}
                            onApprove={handleExpressApprove}
                          />
                        ))}
                      {showGooglePay &&
                        (offer.paymentGateway === 'nmi' ? (
                          <NmiGooglePayButton
                            checkoutTotal={state.orderTotal}
                            orderData={state}
                            offer={offer}
                            onApprove={handleExpressApprove}
                          />
                        ) : (
                          <GooglePayButton
                            clientToken={clientToken}
                            checkoutTotal={state.orderTotal}
                            orderData={state}
                            offer={offer}
                            onApprove={handleExpressApprove}
                          />
                        ))}
                    </div>
                  </div>
                  <div className={styles.expressCheckoutDivider}>
                    <span>Or pay with card</span>
                  </div>
                </>
              )}
            {/* Order Form */}
            <div className={styles.orderForm}>
              <div className={styles.stepTitle}>
                <i className='fa-solid fa-user'></i> Customer Information
              </div>
              {/* address inputs */}
              <div className={styles.inputGroup}>
                <label htmlFor='email'>Email Address</label>
                <input
                  placeholder='Email Address'
                  type='email'
                  name='email'
                  value={state.email}
                  className={errors.email ? styles.errorInput : ''}
                  onChange={(e) => {
                    setState({ ...state, email: e.target.value });
                    setErrors((prevErrors) => ({ ...prevErrors, email: null }));
                  }}
                  onBlur={() => handleFieldBlur('email')}
                />
                {errors.email && <div className={styles.errorMessage}>{errors.email}</div>}
              </div>

              <div className={styles.inputGroup}>
                <label htmlFor='firstName'>First Name</label>
                <input
                  placeholder='First Name'
                  type='text'
                  name='firstName'
                  value={state.firstName}
                  className={errors.firstName ? styles.errorInput : ''}
                  onChange={(e) => {
                    setState({ ...state, firstName: e.target.value });
                    setErrors((prevErrors) => ({ ...prevErrors, firstName: null }));
                  }}
                  onBlur={() => handleFieldBlur('firstName')}
                />
                {errors.firstName && <div className={styles.errorMessage}>{errors.firstName}</div>}
              </div>
              <div className={styles.inputGroup}>
                <label htmlFor='lastName'>Last Name</label>
                <input
                  placeholder='Last Name'
                  type='text'
                  name='lastName'
                  value={state.lastName}
                  className={errors.lastName ? styles.errorInput : ''}
                  onChange={(e) => {
                    setState({ ...state, lastName: e.target.value });
                    setErrors((prevErrors) => ({ ...prevErrors, lastName: null }));
                  }}
                  onBlur={() => handleFieldBlur('lastName')}
                />
                {errors.lastName && <div className={styles.errorMessage}>{errors.lastName}</div>}
              </div>
              <div className={styles.inputGroup}>
                <label htmlFor='phone'>Phone Number</label>
                <div className={styles.phoneInputWrapper}>
                  {/* Country Code Prefix */}
                  <span className={styles.phonePrefix}>
                    {state.shippingCountry === 'GB'
                      ? '+44'
                      : state.shippingCountry === 'US' || state.shippingCountry === 'CA'
                        ? '+1'
                        : ''}
                  </span>
                  {/* Input Field */}
                  <input
                    placeholder={
                      state.shippingCountry === 'GB'
                        ? 'e.g., 7900123456'
                        : state.shippingCountry === 'US' || state.shippingCountry === 'CA'
                          ? 'e.g., 9001234567'
                          : 'Enter phone number'
                    }
                    type='tel'
                    name='phone'
                    value={state.phone.replace(/^\+44|^\+1/, '')} // Display without the prefix
                    className={errors.phone ? styles.errorInput : ''}
                    onChange={(e) => {
                      const prefix =
                        state.shippingCountry === 'GB'
                          ? '+44'
                          : state.shippingCountry === 'US' || state.shippingCountry === 'CA'
                            ? '+1'
                            : '';
                      const phoneNumber = e.target.value.replace(/\D/g, ''); // Remove non-numeric characters
                      setState({ ...state, phone: `${prefix}${phoneNumber}` }); // Store with prefix in state
                      setErrors((prevErrors) => ({ ...prevErrors, phone: null }));
                    }}
                    onBlur={() => handleFieldBlur('phone')}
                  />
                </div>
                {state.shippingCountry === 'GB' && (
                  <div className={styles.inputMessage}>
                    Enter your phone number without the leading <b>0</b>. For example, if your number is{' '}
                    <b>07700123456</b>, simply type <b>7700123456</b>. The international format is
                    automatically applied.
                  </div>
                )}
                {errors.phone && <div className={styles.errorMessage}>{errors.phone}</div>}
              </div>

              <div className={styles.inputGroup}>
                <label htmlFor='shippingAddress1'>Shipping Address</label>
                <input
                  placeholder='Shipping Address'
                  type='text'
                  name='shippingAddress1'
                  value={state.shippingAddress1}
                  className={errors.shippingAddress1 ? styles.errorInput : ''}
                  onChange={(e) => {
                    setState({ ...state, shippingAddress1: e.target.value });
                    setErrors((prevErrors) => ({ ...prevErrors, shippingAddress1: null }));
                  }}
                  onBlur={() => handleFieldBlur('shippingAddress1')}
                />
                {errors.shippingAddress1 && (
                  <div className={styles.errorMessage}>{errors.shippingAddress1}</div>
                )}
              </div>

              <div className={styles.inputGroup}>
                <label htmlFor='shippingCity'>City</label>
                <input
                  placeholder='City'
                  type='text'
                  name='shippingCity'
                  value={state.shippingCity}
                  className={errors.shippingCity ? styles.errorInput : ''}
                  onChange={(e) => {
                    setState({ ...state, shippingCity: e.target.value });
                    setErrors((prevErrors) => ({ ...prevErrors, shippingCity: null }));
                  }}
                  onBlur={() => handleFieldBlur('shippingCity')}
                />
                {errors.shippingCity && <div className={styles.errorMessage}>{errors.shippingCity}</div>}
              </div>

              <div className={styles.inputGroup}>
                <label htmlFor='shippingCountry'>Country</label>
                <div className={styles.formSelect}>
                  <select
                    name='shippingCountry'
                    value={state.shippingCountry}
                    onChange={(e) => setState({ ...state, shippingCountry: e.target.value })}
                  >
                    {offer.shippingCountries.map((country) => (
                      <option key={country.id} value={country.id}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className={styles.formRow}>
                {renderStateSelect()}
                <div className={styles.inputGroup}>
                  <label htmlFor='shippingZip'>Zip/Postal Code</label>
                  <input
                    placeholder='Zip/Postal Code'
                    type='text'
                    name='shippingZip'
                    value={state.shippingZip}
                    className={errors.shippingZip ? styles.errorInput : ''}
                    onChange={(e) => {
                      setState({ ...state, shippingZip: e.target.value });
                      setErrors((prevErrors) => ({ ...prevErrors, shippingZip: null }));
                    }}
                    onBlur={() => handleFieldBlur('shippingZip')}
                  />
                  {errors.shippingZip && <div className={styles.errorMessage}>{errors.shippingZip}</div>}
                </div>
              </div>

              <div className={styles.formShipping}>
                <b>Shipping:</b>
                <span>
                  {selectedProduct?.shippingPrice > 0
                    ? getPriceWithCurrency(selectedProduct?.shippingPrice, offer?.locale, offer?.currency)
                    : 'FREE'}
                </span>
              </div>

              {/* payment inputs */}

              <div className={styles.stepTitle}>
                <i className='fa-solid fa-credit-card'></i> Payment Options
              </div>

              <div className={styles.creditCardBlock}>
                <div className={styles.creditCardBlockHeader}>
                  <b>Credit Card</b>
                  <div className={styles.creditCardBlockHeaderCards}>
                    <img src={visa} alt='visa' />
                    <img src={master} alt='mastercard' />
                    {offer.currency !== 'GBP' && <img src={amex} alt='amex' />}
                  </div>
                </div>
                <div className={styles.inputGroup}>
                  <label htmlFor='creditCardNumber'>Card Number</label>
                  <NumberFormat
                    name='creditCardNumber'
                    format='#### #### #### ####'
                    placeholder='Credit Card Number'
                    value={state.creditCardNumber}
                    className={errors.creditCardNumber ? styles.errorInput : ''}
                    onValueChange={(values) => {
                      const { value } = values;
                      setState({ ...state, creditCardNumber: value });
                      setErrors((prevErrors) => ({ ...prevErrors, creditCardNumber: null }));
                    }}
                    onBlur={() => handleFieldBlur('creditCardNumber')}
                  />
                  {errors.creditCardNumber && (
                    <div className={styles.errorMessage}>{errors.creditCardNumber}</div>
                  )}
                </div>

                {/* Expiration Date Fields */}

                <div className={styles.formRow}>
                  {/* Expiration Month Select */}
                  <div className={styles.inputGroup}>
                    <label htmlFor='expirationMonth'>Expiry Month</label>
                    <div className={styles.formSelect}>
                      <select
                        name='expirationMonth'
                        value={state.expirationMonth}
                        onChange={(e) => {
                          setState({ ...state, expirationMonth: e.target.value });
                          setErrors((prevErrors) => ({ ...prevErrors, expirationMonth: null }));
                        }}
                        onBlur={() => handleFieldBlur('expirationMonth')}
                        className={errors.expirationMonth ? styles.errorInput : ''}
                      >
                        <option value=''>Month</option>
                        <option value='01'>01 - January</option>
                        <option value='02'>02 - February</option>
                        <option value='03'>03 - March</option>
                        <option value='04'>04 - April</option>
                        <option value='05'>05 - May</option>
                        <option value='06'>06 - June</option>
                        <option value='07'>07 - July</option>
                        <option value='08'>08 - August</option>
                        <option value='09'>09 - September</option>
                        <option value='10'>10 - October</option>
                        <option value='11'>11 - November</option>
                        <option value='12'>12 - December</option>
                      </select>
                    </div>
                    {errors.expirationMonth && (
                      <div className={styles.errorMessage}>{errors.expirationMonth}</div>
                    )}
                  </div>
                  {/* Expiration Year Select */}
                  <div className={styles.inputGroup}>
                    <label htmlFor='expirationMonth'>Expiry Year</label>
                    <div className={styles.formSelect}>
                      <select
                        name='expirationYear'
                        value={state.expirationYear}
                        onChange={(e) => {
                          setState({ ...state, expirationYear: e.target.value });
                          setErrors((prevErrors) => ({ ...prevErrors, expirationYear: null }));
                        }}
                        onBlur={() => handleFieldBlur('expirationYear')}
                        className={errors.expirationYear ? styles.errorInput : ''}
                      >
                        <option value=''>Year</option>
                        {Array.from({ length: 20 }, (_, i) => {
                          const year = new Date().getFullYear() + i;
                          return (
                            <option key={year} value={year}>
                              {year}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                    {errors.expirationYear && (
                      <div className={styles.errorMessage}>{errors.expirationYear}</div>
                    )}
                  </div>
                </div>

                {/* CVV Input */}
                <div className={styles.inputGroup}>
                  <label htmlFor='CVV'>CVV</label>
                  <NumberFormat
                    name='CVV'
                    format='####'
                    placeholder='CVV'
                    value={state.CVV}
                    className={errors.CVV ? styles.errorInput : ''}
                    onValueChange={(values) => {
                      const { value } = values;
                      setState({ ...state, CVV: value });
                      setErrors((prevErrors) => ({ ...prevErrors, CVV: null }));
                    }}
                    onBlur={() => handleFieldBlur('CVV')}
                  />
                  {errors.CVV && <div className={styles.errorMessage}>{errors.CVV}</div>}
                </div>

                {/* <div className={styles.inputGroup}>
                  <div className={styles.termsCheckbox}>
                    <input
                      type='checkbox'
                      id='terms'
                      name='terms'
                      checked={state.isTermsAgreed}
                      className={errors.isTermsAgreed ? styles.errorInput : ''}
                      onChange={(e) => {
                        setState({ ...state, isTermsAgreed: e.target.checked });
                        setErrors((prevErrors) => ({ ...prevErrors, isTermsAgreed: null }));
                      }}
                      onBlur={() => handleFieldBlur('isTermsAgreed')}
                    />
                    <label htmlFor='terms'>
                      I agree to the{' '}
                      <b
                        onClick={(e) => {
                          e.preventDefault();
                          onTermsClick();
                        }}
                      >
                        terms and conditions
                      </b>
                    </label>
                  </div>
                  {errors.isTermsAgreed && <div className={styles.errorMessage}>{errors.isTermsAgreed}</div>}
                </div> */}
                <div className={styles.inputGroup}>
                  <div className={styles.termsCheckbox}>
                    <input
                      type='checkbox'
                      id='agreeSms'
                      name='agreeSms'
                      checked={state.agreeSms}
                      className={`${errors.agreeSms ? styles.errorInput : ''} ${styles.bigCheckbox}`}
                      onChange={(e) => {
                        setState({ ...state, agreeSms: e.target.checked });
                      }}
                    />
                    <label htmlFor='agreeSms'>Text Me Shipping Updates and Exclusive Discounts</label>
                  </div>
                  {state.agreeSms && (
                    <div className={styles.smsText}>
                      By clicking this checkbox, you agree to receive marketing text messages from our company
                      at the number provided. Consent is not a condition of purchase. Message and data rates
                      may apply. Message frequency varies. Reply HELP for help or STOP to cancel. View our
                      Privacy Policy and Terms of Service.
                    </div>
                  )}
                </div>
                {/* button */}

                <p className={styles.termsText}>
                  By placing your order, you agree to{' '}
                  <span onClick={() => onTermsClick()}>terms and conditions</span> of service.
                </p>
                <button
                  onClick={() => handleFormSubmit()}
                  className={styles.submitButton}
                  type='button'
                  disabled={isLoading || isCooldownActive}
                >
                  Complete Purchase {''}
                  {isLoading && <i className='fas fa-spinner fa-spin'></i>}
                </button>
                {isCooldownActive && (
                  <div className={styles.declineError}>
                    <p>
                      You've had multiple declined payment attempts. Please wait 15 minutes before trying
                      again or contact customer support.
                    </p>
                  </div>
                )}
                {declineError && !isCooldownActive && (
                  <div className={styles.declineError}>
                    <p>{declineError}</p>
                  </div>
                )}
                {errors.product && <div className={styles.declineError}>{errors.product}</div>}
              </div>

              <div className={styles.checkoutRightFooter}>
                <img src={safe} alt='safe icons' />
                <p>
                  <b>
                    <span>
                      <i className='fa-solid fa-lock'></i> SSL
                    </span>{' '}
                    SECURE PAYMENT
                  </b>
                  <span>Your Personal Details Are Securely Encrypted With 256-Bit-SSL</span>
                </p>

                <MoneyBackGuarantee />

                <div className={styles.checkoutRightFooterBenefits}>
                  {offer.benefits.map((benefit, index) => (
                    <div key={index} className={styles.checkoutRightFooterBenefit}>
                      <i className='fa-solid fa-check'></i>
                      <p dangerouslySetInnerHTML={{ __html: benefit }}></p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            {/* Checkout Right Footer */}
          </article>
        </section>
        {showReviews && <Reviews reviews={reviews} />}
      </div>

      {/* Size Guide Modal */}
      {isSizeGuideOpen && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <span className={styles.close} onClick={() => setIsSizeGuideOpen(false)}>
              &times;
            </span>
            <img
              src={offer.sizeGuideImage}
              alt='Size Guide'
              style={{ maxWidth: '100%', maxHeight: '80vh' }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
